import { Button, Modal, Table, message } from 'antd';
import React, { useEffect, useState } from 'react';
import { getShortDateString } from 'app/utils/datetime';

import { ComplianceService } from 'app/services/compliance';
import { EmailService } from 'app/services/email';
import { LoadingOutlined } from '@ant-design/icons';
import { RefreshOutlined } from '@mui/icons-material';
import WaitAndLoadingWithSpinner from 'app/utils/super-components/waitAndLoadingWithSpinner';
import alert from './assets/AlertTriangle.svg';
import correct from './assets/Check.svg';
import { documentNameMap } from 'app/configs/documentNameMap';
import { getCustomIdFromUrl } from 'app/utils/url';
import home from './assets/home.svg';
import { sendEmail } from 'app/utils/email';
import {
  getCompliancePartnerID,
  isOfficeAdmin,
} from 'app/utils/localStorageHandler/userProfile';
import { partners } from 'app/configs/partner';
import Remarks from 'app/modules/remarks';
import { WirePaymentsService } from 'app/services/wirePayments';
import {
  WIRE_PAYMENT_PRISM_STATUSES,
  WIRE_PAYMENT_PROCESSING_STATUSES,
} from '../WirePayments/utils/useWirePayments';

const headerTextOptions = {
  [WIRE_PAYMENT_PROCESSING_STATUSES.PROCESSING]: 'Under Review',
  [WIRE_PAYMENT_PROCESSING_STATUSES.MULTIPLE_LOOPS_FOUND]:
    'Multiple Loops Found',
  [WIRE_PAYMENT_PROCESSING_STATUSES.LOOP_NOT_FOUND]: 'Loop Not Found',
  [WIRE_PAYMENT_PROCESSING_STATUSES.DOCUMENTS_MISSING]: 'Documents Missing',
  [WIRE_PAYMENT_PRISM_STATUSES.PRISM_NOT_FOUND]: 'Prism Not Found',
  [WIRE_PAYMENT_PRISM_STATUSES.PRISM_FOUND_NOT_UPDATED]: 'Prism Found',
  [WIRE_PAYMENT_PRISM_STATUSES.PRISM_UPDATED]: 'Prism Updated',
  [WIRE_PAYMENT_PRISM_STATUSES.POWERSTREAM]: 'Powerstream',
  [WIRE_PAYMENT_PROCESSING_STATUSES.MAX_APPROVED]: 'Max Approved',
};

const EarnestlyValidationView: React.FC = () => {
  const [earnestly, setEarnestly] = useState<any>(null);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [modalPdfUrl, setModalPdfUrl] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [loadingStates, setLoadingStates] = useState({
    page: true,
    transaction: false,
    button: false,
  });
  const [loadingDocuments, setLoadingDocuments] = useState<
    Record<string, boolean>
  >({});
  const [isContractCare, setIsContractCare] = useState<boolean | null>(null);

  const isAdmin = localStorage.getItem('Admin');
  const [emailLoading, setEmailLoading] = useState(false);

  const headerStatus =
    earnestly?.processingStatus?.status || (earnestly?.processingStatus as any);

  const isValidStatus = (
    status: any
  ): status is WIRE_PAYMENT_PROCESSING_STATUSES => {
    return Object.values(WIRE_PAYMENT_PROCESSING_STATUSES).includes(status);
  };

  const dropDownData: any[][] = [
    [
      {
        label: 'Branch Office',
        value: earnestly?.transactionOffice?.name,
      },
      getCompliancePartnerID() !== partners.JMG && {
        label: 'MLS Status',
        value: loadingStates.transaction
          ? 'Loading...'
          : transactionDetails?.mlsDocument?.mlsStatus ||
            'No active listing in last 6 months',
        buttonOnLabel: true,
        buttonOnLabelIcon: loadingStates.transaction ? (
          <LoadingOutlined spin />
        ) : (
          <RefreshOutlined style={{ fontSize: 'large' }} />
        ),
        onButtonOnLabelClick: () => {
          if (!loadingStates.transaction) {
            fetchTransactionDetails(earnestly?.transactionId);
          }
        },
      },
    ],
    [
      { label: 'Agent Name', value: earnestly?.primaryAgent?.name },
      {
        label: 'Dotloop Link',
        text: earnestly?.transaction?.externalSourceID
          ? `https://www.dotloop.com/m/loop?viewId=${earnestly?.transaction?.externalSourceID}`
          : 'N/A',
        value: earnestly?.transaction?.externalSourceID
          ? `https://www.dotloop.com/m/loop?viewId=${earnestly?.transaction?.externalSourceID}`
          : 'N/A',
        isLink: !!earnestly?.transaction?.externalSourceID,
      },
    ],
  ];

  const setLoadingState = (key: keyof typeof loadingStates, value: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: value }));
  };

  const fetchTransactionDocument = async (
    docId: string,
    transactionId: string
  ) => {
    try {
      const response = await ComplianceService.getDocumentByID(
        transactionId,
        docId
      );
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      return URL.createObjectURL(pdfBlob);
    } catch {
      return 'No Document found';
    }
  };

  const fetchTransactionDetails = async (transactionId: string) => {
    try {
      setLoadingState('transaction', true);
      const response =
        await ComplianceService.getUpdatedMlsStatus(transactionId);
      setTransactionDetails(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      return null;
    } finally {
      setLoadingState('transaction', false);
    }
  };

  const handleViewImage = async (docId: string) => {
    setLoadingDocuments((prev) => ({ ...prev, [docId]: true }));
    const doc = await fetchTransactionDocument(docId, earnestly.transactionId);
    setModalPdfUrl(doc);
    setIsModalVisible(true);
    setLoadingDocuments((prev) => ({ ...prev, [docId]: false }));
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    if (modalPdfUrl) URL.revokeObjectURL(modalPdfUrl);
  };

  const dropDownCell = () =>
    dropDownData.map((row, rowIndex) => (
      <div
        key={rowIndex}
        className={`flex p-4 gap-16 ${rowIndex === 0 ? 'border-b border-[#dfe3eb]' : ''} ${rowIndex === 1 ? 'pb-4' : ''}`}
      >
        {row.map((item, index) => (
          <div key={index} className="flex flex-col gap-1">
            <div className="flex items-center">
              <span className="text-[#2b3340] text-sm font-medium">
                {item.label}
              </span>
              {item?.buttonOnLabel && (
                <div
                  onClick={(event) => {
                    event.stopPropagation();
                    item.onButtonOnLabelClick && item.onButtonOnLabelClick();
                  }}
                  className="ml-2 cursor-pointer flex items-center"
                >
                  {item.buttonOnLabelIcon}
                </div>
              )}
            </div>
            {item.isLink ? (
              <a
                href={item.value}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue text-sm font-medium"
              >
                {item.text}
              </a>
            ) : (
              <span className="text-[#57677e] text-sm font-medium">
                {item.value}
              </span>
            )}
          </div>
        ))}
      </div>
    ));

  const handleSaveNote = async () => {
    try {
      setLoadingState('button', true);
      await new WirePaymentsService().patchRemarks(earnestly?._id, notes);
      message.success('Notes saved successfully!');
    } catch (error) {
      message.error('Error saving notes');
    } finally {
      setLoadingState('button', false);
    }
  };

  const openSendEmail = async () => {
    const status =
      earnestly?.processingStatus?.status || earnestly?.processingStatus || '';
    const recipient = window.location.href.includes('test.maxhome.ai')
      ? 'support@maxhome.ai'
      : earnestly?.primaryAgent?.email || '';
    const recepientName = earnestly?.primaryAgent?.name?.split(' ')[0] || '';
    const address = earnestly?.transaction.name;
    const loopLink = earnestly?.transaction?.externalSourceID
      ? `https://www.dotloop.com/m/loop?viewId=${earnestly?.transaction?.externalSourceID}`
      : '';
    const loopLinkText = loopLink ? `[(${loopLink})]` : '';

    let subject = '';
    let body = `Hi ${recepientName},\n\n`;

    // Set email based on status
    switch (status) {
      case WIRE_PAYMENT_PROCESSING_STATUSES.LOOP_NOT_FOUND:
        subject = `${address} | Earnestly Deposit Received | Loop Not Found`;
        body += `We've received an Earnestly deposit for your listing at ${address}, but we cannot locate your loop. When you have a moment, please submit your documents for review in dotloop as "Under Contract" once they are ready. If you haven't yet done so, please create your transaction in Prism and submit it to CSS.\n\n`;
        body += `You can access Prism at prism.bairdwarner.com. For additional assistance, please review the quick reference document[https://docs.google.com/document/d/1yDGf0UupGkuZu0g0X2Xu1UHWoDzutjSV_152eXC7PRk/edit?usp=sharing] and contact your office staff if you have any questions or encounter any issues while creating your transaction.\n\n`;
        body += `Thank you, and congratulations on your contract!\nCentral Support Services\ncentralsupportservices@bairdwarner.com\n630.724.4727`;
        break;

      case WIRE_PAYMENT_PROCESSING_STATUSES.DOCUMENTS_MISSING:
        subject = `${address} | Earnestly Received | Contract Missing from Loop`;
        body += `We've received an Earnestly deposit for your listing at ${address}; however, we could not find a contract in your loop that matches the buyer's name. When you have a moment, please upload the contract to your loop ${loopLinkText} and submit for review so Sales Closing can process the funds.\n\n`;
        body += `If you haven't yet done so, please create your transaction in Prism and submit it to CSS.\n\n`;
        body += `You can access Prism at prism.bairdwarner.com. For additional assistance, please review the quick reference document [https://docs.google.com/document/d/1yDGf0UupGkuZu0g0X2Xu1UHWoDzutjSV_152eXC7PRk/edit?usp=sharing] and contact your office staff if you have any questions or encounter any issues while creating your transaction.\n\n`;
        body += `Thank you, and congratulations on your contract!\nCentral Support Services\ncentralsupportservices@bairdwarner.com\n630.724.4727`;
        break;

      // case WIRE_PAYMENT_PRISM_STATUSES.VALIDATION_FAILED:
      //   subject = `${address} | Earnestly Deposit Received | Discrepancy in Amount`;
      //   body += `We've received an Earnestly deposit for your listing at ${address}. Upon review, we found that the amount of the Earnestly deposit does not match the amount due in your contract; view loop ${loopLinkText}. When you have a moment, please advise on how to proceed.\n\n`;
      //   body += `Thank you, and congratulations on your contract!\nCentral Support Services\ncentralsupportservices@bairdwarner.com\n630.724.4727`;
      //   break;

      default:
        break;
    }

    sendEmail(
      recipient,
      subject,
      body,
      window.location.href.includes('test.maxhome.ai')
        ? 'helpdesk@maxhome.ai'
        : 'centralsupportservices@bairdwarner.com'
    );
  };

  const formColumns = [
    {
      title: 'Name',
      key: 'name',
      width: '50%',
      render: (_: any, record: any) =>
        documentNameMap[record.type] || record.type,
    },
    {
      title: 'Action',
      key: 'action',
      width: '30%',
      render: (_: any, record: any) => (
        <Button
          type="primary"
          onClick={() => handleViewImage(record._id)}
          loading={loadingDocuments[record._id]}
        >
          View Document
        </Button>
      ),
    },
  ];

  useEffect(() => {
    const paymentID = getCustomIdFromUrl('paymentID');
    if (paymentID) {
      new WirePaymentsService()
        .getWirePaymentById(paymentID)
        .then((response: any) => {
          const data = response?.data?.data;
          setEarnestly(data);
          setNotes(data?.remarks || '');
          setLoadingState('page', false);

          if (data?.document) {
            fetchTransactionDocument(data.document, data.transactionId).then(
              setPdfUrl
            );
          }
          fetchTransactionDetails(data.transactionId);

          // Check for ContractCare after getting earnestly data
          if (data?.transactionId) {
            new WirePaymentsService()
              .checkContractCare(data.transactionId)
              .then((response: any) => {
                setIsContractCare(response.data.isContractCare);
              })
              .catch((error) => {
                console.error('Error checking ContractCare:', error);
                setIsContractCare(false); // Set to false on error to allow email sending
              });
          }
        });
    }
  }, []);
  const handleSendStatusEmail = async () => {
    try {
      // const status =
      //   earnestly?.processingStatus?.status ||
      //   earnestly?.processingStatus ||
      //   '';
      // const emailType = `CHECK_VALIDATION_STATUS_${status}`;

      // const response = await EmailService.getEmailLogs(
      //   earnestly?._id,
      //   'Check',
      //   [emailType],
      //   {}
      // );

      // const emailAlreadySent =
      //   response.data.emailLogs?.data.length > 0 || false;

      const sendEmail = async () => {
        try {
          setEmailLoading(true);
          await new WirePaymentsService().sendWirePaymentEmail(earnestly?._id);
          message.success('Email sent successfully!');
        } catch (error: any) {
          message.error(
            error?.response?.data?.message || 'Error sending status email'
          );
        } finally {
          setEmailLoading(false);
        }
      };

      // if (emailAlreadySent) {
      //   Modal.confirm({
      //     title: 'Email Already Sent',
      //     content:
      //       'An email notification has already been sent to the agent for this check. Would you like to resend the email?',
      //     okText: 'Resend Email',
      //     cancelText: 'Cancel',
      //     onOk: () => sendEmail(),
      //   });
      // } else {
      Modal.confirm({
        title: 'Send Auto Email',
        content: 'This will send a notification email to the agent.',
        okText: 'Send',
        cancelText: 'Cancel',
        onOk: () => sendEmail(),
      });
      // }
    } catch (error) {
      console.error('Error checking email logs:', error);
      message.error('Error checking email status');
    }
  };

  const component = (
    <>
      <div className="text-base font-medium bg-[#f9fafc] rounded-lg p-4 border-2 border-[#e0e0e0]">
        <div className="text-xl font-semibold pb-4 border-b-2 border-[#e0e0e0]">
          Overview
        </div>
        <div className="rounded-xl mt-4 mb-4 shadow-[0_2px_10px_rgba(0,0,0,0.1)]  border border-[#e3e8ee]">
          <div
            className={`flex items-center h-11 rounded-t-xl ${
              earnestly?.processingStatus?.status !==
              WIRE_PAYMENT_PROCESSING_STATUSES.MAX_APPROVED
                ? 'bg-[#ffb800] text-[#251A00]'
                : 'bg-[#29845A] text-[#F8FFFB]'
            }`}
          >
            <img
              src={
                earnestly?.processingStatus?.status ||
                earnestly?.processingStatus !==
                  WIRE_PAYMENT_PROCESSING_STATUSES.MAX_APPROVED
                  ? alert
                  : correct
              }
              alt="Alert Icon"
              className="pl-4 flex flex-col gap-2 text-sm mr-2"
            />
            {earnestly?.errorFields?.length > 0
              ? `${earnestly?.errorFields?.length} issues found |`
              : earnestly?.processingStatus?.status ||
                  earnestly?.processingStatus ===
                    WIRE_PAYMENT_PROCESSING_STATUSES.DOCUMENTS_MISSING ||
                  earnestly?.processingStatus?.status ||
                  earnestly?.processingStatus ===
                    WIRE_PAYMENT_PROCESSING_STATUSES.MULTIPLE_LOOPS_FOUND ||
                  earnestly?.processingStatus ===
                    WIRE_PAYMENT_PROCESSING_STATUSES.LOOP_NOT_FOUND ||
                  earnestly?.processingStatus?.status ||
                  earnestly?.processingStatus ===
                    WIRE_PAYMENT_PRISM_STATUSES.PRISM_NOT_FOUND
                ? ''
                : 'No issues found |'}
            <>
              <span className="ml-2">
                {isValidStatus(headerStatus)
                  ? headerTextOptions[headerStatus]
                  : ''}
              </span>
              {headerStatus ===
              WIRE_PAYMENT_PROCESSING_STATUSES.DOCUMENTS_MISSING ? (
                <span className="ml-2">
                  {' : '}
                  {earnestly?.processingStatus?.missingDocs &&
                    earnestly?.processingStatus?.missingDocs
                      ?.map((item: any) => {
                        if (Array.isArray(item)) {
                          return item
                            .map(
                              (doc: any) => documentNameMap[doc] || doc || ''
                            )
                            .filter(Boolean)
                            .join(' or ');
                        }
                        return documentNameMap[item] || item || '';
                      })
                      .filter(Boolean)
                      .join(', ')}
                </span>
              ) : null}
            </>
          </div>
          <ul className="pl-8 text-sm flex flex-col gap-2">
            {/* Check if there are required errors and render them */}
            {earnestly?.errorFields?.filter(
              (error: any) => error.severity === 'required'
            )?.length > 0 && (
              <>
                {/* Required Tag */}
                <div className="font-bold pl-2 border-l-4 border-red-500">
                  Critical errors :
                </div>
                {/* Required Errors List */}
                {earnestly.errorFields
                  .filter((error: any) => error.severity === 'required')
                  .map((error: any, index: number) => (
                    <li
                      className="ml-2"
                      key={`required-${index}`}
                      dangerouslySetInnerHTML={{
                        __html: error.descriptionFormattedString,
                      }}
                    />
                  ))}
              </>
            )}

            {/* Check if there are optional errors and render them */}
            {earnestly?.errorFields?.filter(
              (error: any) => error.severity === 'optional'
            )?.length > 0 && (
              <>
                <div className="font-bold pl-2 border-l-4 border-[#a4a449]">
                  Non-critical errors :
                </div>
                {/* Optional Errors List */}
                {earnestly.errorFields
                  .filter((error: any) => error.severity === 'optional')
                  .map((error: any, index: number) => (
                    <li
                      className="ml-2"
                      key={`optional-${index}`}
                      dangerouslySetInnerHTML={{
                        __html: error.descriptionFormattedString,
                      }}
                    />
                  ))}
              </>
            )}

            {/* Show check completion message when no errors */}
            {!earnestly?.errorFields?.length &&
              earnestly?.processingStatus?.status ===
                WIRE_PAYMENT_PROCESSING_STATUSES.MAX_APPROVED && (
                <li>Check is ready for submission.</li>
              )}
          </ul>

          {!isOfficeAdmin() ? (
            <div className="flex gap-6 pl-4 pb-4">
              <Button
                type="primary"
                onClick={openSendEmail}
                disabled={
                  loadingStates.transaction ||
                  isContractCare === null ||
                  isContractCare
                }
              >
                Send Email
              </Button>{' '}
              {isAdmin &&
                [
                  WIRE_PAYMENT_PRISM_STATUSES.PRISM_NOT_FOUND,
                  WIRE_PAYMENT_PRISM_STATUSES.PRISM_UPDATED,
                  WIRE_PAYMENT_PRISM_STATUSES.POWERSTREAM,
                ].includes(
                  earnestly?.processingStatus?.externalStatuses?.PRISM
                ) && (
                  <Button
                    type="primary"
                    onClick={handleSendStatusEmail}
                    loading={emailLoading}
                    disabled={isContractCare === null || isContractCare}
                    className="ml-2"
                  >
                    Send email to agent
                  </Button>
                )}
            </div>
          ) : null}
        </div>
        <div className="p-4 rounded-xl mb-4 shadow-[0_2px_10px_rgba(0,0,0,0.1)] border border-[#e3e8ee]">
          <div className="font-bold text-lg pb-4 border-b border-[#dfe3eb] flex items-center">
            <img src={home} alt="Home Icon" className="homeIcon mr-2" />
            {earnestly?.transaction?.name || 'N/A'}
          </div>
          {dropDownCell()}
        </div>
        <div className="p-4 rounded-xl mb-4 shadow-[0_2px_10px_rgba(0,0,0,0.1)] border border-[#e3e8ee]">
          <div className="flex flex-col mb-5">
            <span className="text-base">Wire Payment</span>
            {pdfUrl && pdfUrl !== 'No Document found' && (
              <span className="text-sm font-normal">
                {`Sent ${getShortDateString(earnestly?.createdAt)}` || 'N/A'}
              </span>
            )}
            <div className="mt-2">
              {pdfUrl ? (
                pdfUrl !== 'No Document found' ? (
                  <embed
                    src={`${pdfUrl}`}
                    type="application/pdf"
                    width="100%"
                    height="600px"
                  />
                ) : (
                  <p>{pdfUrl}</p>
                )
              ) : (
                <p>Loading PDF...</p>
              )}
            </div>
          </div>
          <div className="py-5 border-t border-[#e0e0e0] border-b">
            <label className="text-base">Remarks</label>
            <textarea
              className="w-fill-available my-2 h-24 border border-[#e0e0e0] rounded-md p-2 text-sm"
              placeholder="Add notes here ..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={isOfficeAdmin()}
            ></textarea>
            <Button
              type="primary"
              onClick={handleSaveNote}
              loading={loadingStates.button}
              disabled={isOfficeAdmin()}
            >
              {' '}
              Save note
            </Button>
          </div>
          <div className="py-5 border-t border-[#e0e0e0] border-b">
            <span className="pb-4 text-lg">Forms & contract</span>
            <Table
              dataSource={
                earnestly?.transaction?.documents.filter(
                  (doc: any) =>
                    doc.type === 'IL_MAIN' || doc.type === 'IL_BW_RRPSC'
                ) || []
              }
              columns={formColumns}
              rowKey="_id"
              pagination={false}
              locale={{ emptyText: 'No forms or contracts uploaded' }}
            />
          </div>
        </div>
      </div>
      <Modal
        title="Document Preview"
        visible={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        centered
        width="80%"
      >
        {modalPdfUrl ? (
          <embed
            src={`${modalPdfUrl}`}
            type="application/pdf"
            width="100%"
            height="500px"
          />
        ) : (
          <p>No document found</p>
        )}
      </Modal>
      {/* <Remarks
        entityId={earnestly?._id}
        entityType="Check"
        position="bottom-right"
      /> */}
    </>
  );

  return (
    <WaitAndLoadingWithSpinner
      loading={loadingStates.page}
      component={component}
    />
  );
};

export default EarnestlyValidationView;
